import { PatientService } from "@/services/api/patient.api.service"
import {copyObject} from "@/extensions/prototypes/prototypes"
import VisitService from "@/services/visit.service"
import {ensureGroupIndexes, fetchVisitTeeth} from "@/services/teeth.helper"
import {CalendarService} from "@/services/api/calendar.api.service"
import {findEGISZMKBOption} from "@/services/egisz.helper"

let egiszMKBOptionsRequest = null // in-flight request, so parallel callers don't load the dictionary several times

const state = {
    patient: null,
    visit: null,
    planTreatment: null,
    planVisit: null,
    planTreatmentChangedId: null,
    planTreatmentVisitChangedId: null,
    printAgreements: false,
    selectedTemplate: null,
    plannedCall: null,
    advanceForRefill: null,
    egiszMKBOptions: [], // EGISZ MKB dictionary as { mkb_code, title } - for clinics with diagnosis_dictionary = egisz
}

const getters = {
    recordOpened(state) {
        return !!state.visit?.opened_visit?.id
    },
    visitHasPayments(state) {
        const visit = state.visit
        if(!visit) return true
        const hasPrice = visit.price > 0 || visit.additional_discount > 0 // additional discount for cases with 100% discount
        const hasPayments = (visit.payments || []).length > 0
        return hasPrice && hasPayments
    },
    getVisitTeethWithServicesOrDiagnoses(state, getters) {
        const recordTeeth = getters.recordTeethObjects
        return recordTeeth
            .filter(tooth => (tooth.diagnoses || []).length || (tooth.egisz_diagnoses || []).length || (tooth.services || []).length)
            .map(tooth => tooth.teeth)
    },
    getVisitTeethWithServices(state, getters) {
        const recordTeeth = getters.recordTeethObjects
        return recordTeeth
            .filter(tooth => (tooth.services || []).length)
            .map(tooth => tooth.teeth)
    },
    teethGrouped(state, getters) {
        const recordTeeth = getters.recordTeethObjects
        const groups = []
        for(const tooth of recordTeeth) {
            const index = tooth.group_index
            if(!groups[index]) groups[index] = []
            groups[index].push(tooth)
        }
        return groups
    },
    recordTeethObjects(state) {
        return fetchVisitTeeth(state.visit)
    },
    recordTeeth(state) {
        if(state.visit?.type === 'plan_visit') { // for plan treatment complex
            return (state.visit?.teeth || []).map(a => a.tooth)
        }
        return (state.visit?.record_teeth || []).map(a => a.teeth) // for standard visit
    },
    recordTeethServices(state, getters) {
        return getters.recordTeethObjects.map(o => o.services || []).flat()
    },
    recordTeethServicesSorted(state, getters) {
        const ids = []
        const res = []
        for(const tooth of getters.recordTeethObjects) {
            for(const service of (tooth.services || [])) {
                if(!ids.includes(service.id)) {
                    ids.push(service.id)
                    res.push(service)
                }
            }
        }
        return res
    },
    recordTeethServicesWithQuantity(state, getters) {
        const services = {}
        for(const tooth of getters.recordTeethObjects) {
            for(const service of (tooth.services || [])) {
                if(!services[service.id]) {
                    services[service.id] = {
                        qty: 0,
                        service
                    }
                }
                services[service.id].qty++
            }
        }
        return Object.values(services)
    },
    recordTeethComplexServices(state, getters) {
        return getters.recordTeethObjects.map(o => o.complex_services).flat()
    },
    recordTeethDiagnoses(state, getters) {
        return getters.recordTeethObjects.map(o => o.diagnoses).flat()
    },
    recordTeethPreliminaryDiagnoses(state, getters) {
        return getters.recordTeethObjects.map(o => o.preliminary_diagnoses).flat()
    },
    recordTeethEGISZDiagnoses(state, getters) {
        return getters.recordTeethObjects.map(o => o.egisz_diagnoses || []).flat()
    },
    recordTeethEGISZPreliminaryDiagnoses(state, getters) {
        return getters.recordTeethObjects.map(o => o.egisz_preliminary_diagnoses || []).flat()
    },
    // Diagnoses already attached to the visit win over the clinic setting, so visits made before the
    // clinic switched dictionaries keep working with the source they were filled with
    visitDiagnosisSource(state, getters, rootState) {
        if(getters.recordTeethDiagnoses.length || getters.recordTeethPreliminaryDiagnoses.length) return 'dentist_plus'
        if(getters.recordTeethEGISZDiagnoses.length || getters.recordTeethEGISZPreliminaryDiagnoses.length) return 'egisz'
        return rootState.auth.clinic?.diagnosis_dictionary === 'egisz' ? 'egisz' : 'dentist_plus'
    },
    recordHasServicesForRequirementOrder(state, getters) {
        return getters.recordTeethServices.some(obj => obj.dental_work_required !== null)
    },
    recordMaterialsByServiceIds(state, getters) {
        const arr = {
            'noService': []
        }
        for(const tooth of getters.recordTeethObjects) {
            for(const material of tooth.materials) {
                const serviceId = material.pivot?.service_id || 'noService'
                if(!arr[serviceId]) arr[serviceId] = []
                arr[serviceId].push(material)
            }
        }
        return arr
    },
    recordPackagesByServiceIds(state, getters) {
        const arr = {
            'noService': []
        }
        for(const visitTooth of getters.recordTeethObjects) {
            for(const pack of visitTooth.packages) {
                const serviceId = pack.pivot?.service_id || 'noService'
                if(!arr[serviceId]) arr[serviceId] = []
                arr[serviceId].push(pack)
            }
        }
        return arr
    },
    visitCostServicesWithoutDiscount(state, getters) {
        let total = 0
        const recordTeeth = getters.recordTeethObjects
        for(const tooth of recordTeeth) {
            for(const service of (tooth.services || [])) {
                total += Number(service.pivot?.price_original || 0)
            }
        }
        return total
    },
    visitFinalAmount(state, getters) {
        return getters.visitCostServicesWithDiscount - (state.visit.additional_discount || 0)
    },
    visitTeethDiscounts(state, getters) {
        let total = 0
        const recordTeeth = getters.recordTeethObjects
        for(const tooth of recordTeeth) {
            for(const service of (tooth.services || [])) {
                total += VisitService.calculateDiscountOfService(service)
            }
        }
        return total
    },
    visitCostServicesWithDiscount(state, getters) {
        return getters.visitCostServicesWithoutDiscount - getters.visitTeethDiscounts
    },
    recordOpenedClosedStatus(state) {
        const visit = state.visit
        if(!visit) return null
        if(visit.opened_visit?.id) return 'opened'
        if(visit.closed_visit?.id) return 'closed'
        return 'new'
    },
    serviceIdsWithLabServicesAttached(state) {
        const visit = state.visit
        return (visit?.laboratoryServices || []).map(a => a.service_id)
    },
    toothHasLabServices: (state, getters) => (toothName) => {
        if(!toothName) return false
        toothName = toothName.toString()
        const tooth = (getters.recordTeethObjects || []).find(t => t.tooth === toothName || t.teeth === toothName)
        if(!tooth) return false
        const toothServiceIds = (tooth?.services || []).map(s => s.id)
        if(!toothServiceIds.length) return false
        return getters.serviceIdsWithLabServicesAttached.some(element => toothServiceIds.includes(element))
    }
}

const mutations = {
    setEGISZMKBOptions(state, options) {
        state.egiszMKBOptions = options
    },
    setPatient(state, patient) {
        state.patient = patient
    },
    setVisit(state, visit) {
        ensureGroupIndexes(visit?.record_teeth || [])
        if(visit && !visit.record_additional_info) {
            visit.record_additional_info = {}
        }
        state.visit = visit
    },
    setPlanTreatment(state, planTreatment) {
        state.planTreatment = planTreatment
        const visits = state.planTreatment?.visits || []
        visits.forEach(v => {
            ensureGroupIndexes(v.teeth || [])
        })
    },
    // UNUSED: not referenced anywhere (2026-10-06), commented out - remove later
    // setPlanVisit(state, planVisit) {
    //     state.planVisit = planVisit
    //     ensureGroupIndexes(state.planVisit?.teeth || [])
    // },
    setPrintAgreements(state, value) {
        state.printAgreements = value
    },
    setSelectedTemplate(state, value) {
        state.selectedTemplate = value
    },
    setPlannedCall(state, value) {
        state.plannedCall = value
    },
    setAdvanceForRefill(state, value) {
        state.advanceForRefill = value
    },
    setPlanTreatmentChanged(state, value) {
        state.planTreatmentChangedId = value
    },
    setPlanTreatmentVisitChanged(state, value) {
        state.planTreatmentVisitChangedId = value
    },
}

const actions = {
    async loadEGISZMKBOptions({ state, commit }) {
        if(state.egiszMKBOptions.length) return state.egiszMKBOptions
        if(!egiszMKBOptionsRequest) {
            egiszMKBOptionsRequest = CalendarService.getEGISZDictionaries('getMKBCodes')
                .then(res => {
                    const options = (res.data?.getMKBCodes || []).map(o => ({ mkb_code: o.egisz_id, title: o.title }))
                    commit('setEGISZMKBOptions', options)
                    return options
                })
                .finally(() => {
                    egiszMKBOptionsRequest = null
                })
        }
        return egiszMKBOptionsRequest
    },
    getPatient(context, id) {
        return new Promise((resolve, reject) => {
            PatientService.getPatient(id).then(data => {
                let d = data.data
                context.commit('setPatient', d)
                resolve(d)
            }).catch(err => {
                reject(err.response ? err.response.data.errors : [])
            })
        })
    },
    async updatePatientDataForPayment(context, patient) {
        let patientData = copyObject(patient)
        const patientLatest = (await PatientService.getPatient(patient.id)).data
        patientData.parent_family_accounts = patientLatest.parent_family_accounts
        patientData.discount = patientLatest.discount
        patientData.deposit_by_accounts = patientLatest.deposit_by_accounts
        patientData.insurance_polises = patientLatest.insurance_polises
        patientData.representative_fio = patientLatest.representative_fio
        patientData.representative_id = patientLatest.representative_id
        patientData.use_bonuses = patientLatest.use_bonuses
        context.commit('setPatient', patientData)
    },
    getClientVisit(context, { id, visit_id }) {
        return new Promise((resolve, reject) => {
            PatientService.getClientVisit(id, visit_id).then(data => {
                const visit = data.data
                context.commit('setVisit', visit)
                resolve(visit)
            }).catch(err => {
                reject(err.response ? err.response.data.errors : [])
            })
        })
    },
    getClientPlan(context, { id, plan_id }) {
        return new Promise((resolve, reject) => {
            PatientService.getClientPlan(id, plan_id).then(data => {
                const d = data.data
                d.visits.forEach(item => {
                    item.insurance_polis = d.insurance_polis
                    item.type = 'plan_visit'
                })
                context.commit('setPlanTreatment', d)
                resolve(d)
            }).catch(err => {
                reject(err.response ? err.response.data.errors : [])
            })
        })
    },
    async addDiagnosisToTooth(context, { diagnosis, toothName }) {
        const recordTeeth = context.rootGetters.recordTeethObjects
        const tooth = recordTeeth.find(t => t.teeth === toothName || t.tooth === toothName) // for record or plan
        if(!tooth) return
        if(context.getters.visitDiagnosisSource === 'egisz') {
            const options = await context.dispatch('loadEGISZMKBOptions')
            const option = findEGISZMKBOption(options, diagnosis.mkb_code)
            if(!option) return
            if(!tooth.egisz_diagnoses) tooth.egisz_diagnoses = []
            if(!tooth.egisz_diagnoses.find(d => d.mkb_code === option.mkb_code)) {
                tooth.egisz_diagnoses.push({ ...option })
            }
            return
        }
        if(tooth.diagnoses.findIndex(obj => Number(obj.id) === Number(diagnosis.id)) === -1) {
            tooth.diagnoses.push(diagnosis)
        }
    },
    addComplexServiceToTooth(context, { complexService, toothName }) {
        const recordTeeth = context.rootGetters.recordTeethObjects
        for(const tooth of recordTeeth) {
            if(tooth.teeth === toothName || tooth.tooth === toothName) { // for record or plan
                if(tooth.complex_services.findIndex(obj => Number(obj.id) === Number(complexService.id)) === -1) {
                    tooth.complex_services.push(complexService)
                }
                break
            }
        }
    },
    async toggleRecordTeeth({ rootState, dispatch, getters }, tooth) {
        const recordTeeth = getters.recordTeethObjects
        const ind = recordTeeth.findIndex(obj => obj.teeth === tooth || obj.tooth === tooth)
        if (ind === -1) {
            let groupIndex = !recordTeeth.length ? 0 : (Math.max(...recordTeeth.map(o => Number(o.group_index || 0))) + 1)
            recordTeeth.push({
                group_index: groupIndex,
                preliminary_diagnoses: [],
                diagnoses: [],
                egisz_preliminary_diagnoses: [],
                egisz_diagnoses: [],
                services: [],
                packages: [],
                materials: [],
                complex_services: [],
                description: null,
                recommendations: null,
                medication: null,
                plansurvey: null,
                plancure: null,
                teeth: tooth, // teeth in visit
                tooth: tooth // tooth in plan visit
            })
            if(rootState.teeth.sameGroupSelectedTooth && groupIndex > 0) { // then we need to copy all it's data
                dispatch('moveToothToOtherGroup', { tooth, groupIndex: groupIndex - 1 })
            }
            if(rootState.teeth.isShiftPressed) {
                rootState.teeth.sameGroupSelectedTooth = true
            }
        } else {
            const toothToDelete = copyObject(recordTeeth[ind])
            recordTeeth.splice(ind, 1)
            await dispatch('moveMaterialsAndPackagesFromDeletedTooth', { tooth: toothToDelete })
        }
    },
    moveToothToOtherGroup({ getters }, { tooth, groupIndex }) {
        const toothAsExample = copyObject((getters.recordTeethObjects).find(obj => obj.group_index === groupIndex))
        const currentTooth = getters.recordTeethObjects.find(obj => obj.teeth === tooth)
        if(!currentTooth) return
        currentTooth.group_index = groupIndex
        currentTooth.preliminary_diagnoses = toothAsExample?.preliminary_diagnoses || []
        currentTooth.complex_services = toothAsExample?.complex_services || []
        currentTooth.diagnoses = toothAsExample?.diagnoses || []
        currentTooth.egisz_preliminary_diagnoses = toothAsExample?.egisz_preliminary_diagnoses || []
        currentTooth.egisz_diagnoses = toothAsExample?.egisz_diagnoses || []
        currentTooth.services = toothAsExample?.services || []
        currentTooth.description = toothAsExample?.description || null
        currentTooth.recommendations = toothAsExample?.recommendations || null
        currentTooth.medication = toothAsExample?.medication || null
        currentTooth.plansurvey = toothAsExample?.plansurvey || null
        currentTooth.plancure = toothAsExample?.plancure || null
    },
    async moveMaterialsAndPackagesFromDeletedTooth({ dispatch }, { tooth }) {
        for(const material of (tooth.materials || [])) {
            const serviceId = material?.pivot?.service_id
            await dispatch('addMaterialToRandomToothWithService', { serviceId, material })
        }
        for(const pack of (tooth.packages || [])) {
            const serviceId = pack?.pivot?.service_id
            const toothWithServiceId = await dispatch('findToothWithServiceId', { serviceId })
            if(toothWithServiceId) toothWithServiceId.packages.push(pack)
        }
    }
};

export default {
    state,
    actions,
    mutations,
    getters
}
